/**
 * Единая авторизация Eplex (мини-ТЗ P9): тонкие адаптеры к браузеру — cookie,
 * sessionStorage, localStorage, переход по адресу. Правил здесь нет, они в
 * `unifiedAuth`.
 *
 * Отдельный файл нужен, потому что этими же функциями пользуется гард маршрутов,
 * а он не компонент и composable вызвать не может.
 */

import { fetchLoginUrl } from "../../../api/authEntryApi";
import { attribution } from "./authAttribution";
import { clearLegacyUserCache } from "./legacyUserCache";
import { AUTH_TOKEN_COOKIE, isSessionTokenValue, sessionFingerprint } from "./unifiedAuth";

/** Признак «в этом браузере уже была сессия». Живёт между сессиями. */
const RETURNING_VISITOR_KEY = "eplex_auth_seen";

/** Токен, который бэкенд уже отверг. Живёт в рамках вкладки. */
const REJECTED_SESSION_KEY = "eplex_auth_rejected";

export function readCookie(name: string): string | null {
  const prefix = `${name}=`;

  for (const chunk of document.cookie.split(";")) {
    const entry = chunk.trim();
    if (entry.startsWith(prefix)) return decodeURIComponent(entry.slice(prefix.length));
  }

  return null;
}

/**
 * Есть ли сессия единой авторизации — по куке, а не по хранилищу браузера.
 *
 * Кука, которую бэкенд уже отверг, сессией НЕ считается (CAB-680). Иначе получался
 * тупик: на 401 фронт уводил человека на страницу входа, гард на входе видел ту же
 * куку — целую с виду, ведь проверяется только форма JWT, — и возвращал его обратно
 * в кабинет, где каждый запрос снова отвечал 401. Со стороны это ровно то, на что
 * жаловались: «ошибка про сессию есть, а не выкидывает».
 *
 * Кука при этом остаётся нетронутой намеренно: она общая на домен .eplex.ru, и
 * стереть её означало бы выйти за человека из ОСТАЛЬНЫХ продуктов Eplex — там его
 * сессия может быть совершенно жива.
 */
export function hasAuthSession(): boolean {
  const value = readCookie(AUTH_TOKEN_COOKIE);
  if (!isSessionTokenValue(value)) return false;

  return !isTokenRejected(value);
}

/**
 * Запомнить, что бэкенд отверг текущую сессию.
 *
 * Метка привязана к отпечатку токена, а не к факту «был отказ»: вход заново выдаёт
 * другой токен, отпечаток перестаёт совпадать, и метка гаснет сама. Флаг «сессия
 * мертва» без этой привязки пришлось бы снимать руками, а забытое снятие означало
 * бы человека, которого не пускают внутрь после успешного входа.
 */
export function markSessionRejected(): void {
  const fingerprint = sessionFingerprint(readCookie(AUTH_TOKEN_COOKIE));
  if (fingerprint === null) return;

  try {
    sessionStorage.setItem(REJECTED_SESSION_KEY, fingerprint);
  } catch {
    // приватный режим может запрещать запись — тогда метки не будет, и человек
    // просто увидит уведомление о сессии ещё раз
  }
}

/** Отвергал ли бэкенд ту куку, что лежит в браузере прямо сейчас. */
export function isSessionRejected(): boolean {
  return isTokenRejected(readCookie(AUTH_TOKEN_COOKIE));
}

export function clearSessionRejection(): void {
  try {
    sessionStorage.removeItem(REJECTED_SESSION_KEY);
  } catch {
    // см. markSessionRejected
  }
}

function isTokenRejected(value: string | null): boolean {
  const fingerprint = sessionFingerprint(value);
  if (fingerprint === null) return false;

  try {
    return sessionStorage.getItem(REJECTED_SESSION_KEY) === fingerprint;
  } catch {
    return false;
  }
}

/**
 * Человек, который уже входил в этом браузере, уводится на страницу входа сразу.
 * Тот, кто пришёл впервые, сначала видит, что такое Eplex и как завести аккаунт
 * за минуту: он ещё не решил, нужен ли ему вход вообще.
 */
export function markSessionSeen(): void {
  try {
    localStorage.setItem(RETURNING_VISITOR_KEY, "1");
  } catch {
    // без метки новичок увидит страницу-приглашение лишний раз — не ошибка
  }
}

export function isReturningVisitor(): boolean {
  try {
    return localStorage.getItem(RETURNING_VISITOR_KEY) === "1";
  } catch {
    return false;
  }
}

/**
 * Уход на страницу входа Eplex. Полная смена адреса, не SPA-навигация: страница
 * входа живёт на другом домене.
 *
 * Адрес спрашиваем у бэкенда (Ф1): контур знает только он, а сборка фронта одинакова
 * на всех окружениях — из-за этого стенд однажды уводил людей вводить пароль в прод.
 * Оттуда же приходит и защита от петли: второй отказ подряд возвращается без адреса,
 * и тогда уходить некуда — фронт показывает «войти не удалось».
 *
 * `internalPath` — куда вернуть человека внутри ЛК; путь целиком, вместе с query и
 * фрагментом, чтобы глубокая ссылка привела ровно туда, откуда он ушёл.
 *
 * @returns удалось ли уйти. false означает «показывай экран отказа», а не «повтори».
 */
export async function redirectToAuthEntry(internalPath: string): Promise<boolean> {
  const loginUrl = await fetchLoginUrl(internalPath, true);

  if (loginUrl === null) return false;

  // Уходим авторизовываться заново — прежняя сессия больше не наша. Вместе с
  // токенами стираем и опознание пользователя: вернуться может другой человек, а
  // по этой записи собираются ключи сохранённого состояния
  purgeLegacySession();
  clearLegacyUserCache();
  warnIfReturnUnreachable();
  window.location.assign(withAttribution(loginUrl));

  return true;
}

/**
 * Метки источника добавляет фронт, а не сервер: они живут в браузере (sessionStorage) и
 * серверу неизвестны. Адрес при этом остаётся серверным — мы лишь дописываем параметры
 * к тому, что получили.
 */
function withAttribution(loginUrl: string): string {
  const marks = attribution();
  if (Object.keys(marks).length === 0) return loginUrl;

  try {
    const url = new URL(loginUrl);
    for (const [key, value] of Object.entries(marks)) {
      url.searchParams.set(key, value);
    }

    return url.toString();
  } catch {
    // Сервер прислал что-то, что не разбирается как адрес: уходим как есть, решать
    // это не здесь
    return loginUrl;
  }
}

/**
 * Возврат физически не сработает, если открыть ЛК не по https и не под eplex.ru:
 * REG принимает адрес возврата только по https и только с хоста из явного списка,
 * а куки ставятся на домен {{.eplex.ru}}. При провале проверки REG уводит на свой
 * дефолтный адрес — со стороны это выглядит как «вход не сработал», хотя токены
 * уже выпущены.
 *
 * Пишем в консоль, чтобы это выяснялось чтением сообщения, а не экспериментом.
 * Контур в тексте намеренно не называем: его знает только сервер, и захардкоженный
 * домен на стенде назвал бы прод — подсказка из помощи превратилась бы в дезинформацию.
 */
function warnIfReturnUnreachable(): void {
  const { protocol, hostname, origin } = window.location;
  const underEplex = hostname === "eplex.ru" || hostname.endsWith(".eplex.ru");

  if (protocol === "https:" && underEplex) return;

  console.error(
    `Единая авторизация: возврат со страницы входа на ${origin} не сработает. ` +
      "Нужен https и хост под eplex.ru, внесённый в RETURN_TO_ALLOWED_HOSTS у REG; " +
      "куки сессии ставятся на домен .eplex.ru. Локально проверяется только уход на страницу входа.",
  );
}

/** Ключи прежней схемы: ТОКЕНЫ, которыми авторизуется запрос. */
const LEGACY_TOKEN_KEYS = ["access_token", "authTokenForApi"];

/**
 * Стирает токены прежней сессии (CAB-641).
 *
 * Без этого получается подмена личности, и она уже случилась на стенде: человек
 * входит под одним аккаунтом, кука приезжает его, а в localStorage лежит токен от
 * ВЧЕРАШНЕГО входа под другим. Дальше фронт кладёт старый токен в заголовок
 * Authorization, бэкенд авторизует по нему — и человек оказывается предыдущим
 * пользователем. Со стороны выглядит как «вошёл не в свой аккаунт».
 *
 * Запись `_user` здесь НЕ трогаем, хотя она тоже из прежней схемы. Гард зовёт эту
 * функцию на КАЖДОМ переходе, а `_user` — это опознание владельца сохранённого
 * состояния: по нему собираются ключи вкладок проценки, истории поиска, целей
 * подбора. Стирая её на каждом переходе, мы стирали не «след вчерашней сессии», а
 * ключ к своим же данным: после перезагрузки проценка и история читались из ключа
 * без почты, то есть выглядели пустыми, а следующее сохранение затирало настоящую
 * запись пустой (CAB-641 → регресс). Личность живёт ровно столько, сколько сессия:
 * её стирают выход, 401 и уход на страницу входа.
 */
export function purgeLegacySession(): void {
  for (const key of LEGACY_TOKEN_KEYS) {
    try {
      localStorage.removeItem(key);
    } catch {
      // приватный режим может запрещать запись — тогда чистить нечего
    }
  }
}
